import { useState } from "react";
import { KeyRound, Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/utils/dateUtils";
import { errorMessage, useRevokeToken, useTokens, useTokenScopes, type AccessToken } from "./api";
import { MintTokenDialog } from "./MintTokenDialog";
import { TAP_ON_PHONE } from "./classes";

/** MCP access tokens; hidden until the server offers the token API. */
export function McpTokensRow() {
  const scopes = useTokenScopes();
  const tokens = useTokens(Boolean(scopes.data));
  const [minting, setMinting] = useState(false);

  if (!scopes.data) return null;
  const rows = tokens.data ?? [];

  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <div className="flex items-center gap-3">
        <KeyRound className="h-5 w-5 text-muted-foreground" />
        <span className="font-medium">MCP access tokens</span>
        <span className="ml-auto">
          <Button variant="outline" className={TAP_ON_PHONE} onClick={() => setMinting(true)}>
            <Plus className="h-4 w-4" />
            New token
          </Button>
        </span>
      </div>
      {rows.map((token) => (
        <TokenLine key={token.id} token={token} labels={scopes.data!} />
      ))}
      {tokens.isError && (
        <div className="flex items-center gap-2">
          <p role="alert" className="text-xs text-destructive">
            {errorMessage(tokens.error)}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className={TAP_ON_PHONE}
            onClick={() => void tokens.refetch()}
          >
            Try again
          </Button>
        </div>
      )}
      <MintTokenDialog open={minting} onOpenChange={setMinting} scopes={scopes.data} />
    </div>
  );
}

function TokenLine({
  token,
  labels,
}: {
  token: AccessToken;
  labels: { id: string; label: string }[];
}) {
  const [confirming, setConfirming] = useState(false);
  const revoke = useRevokeToken();
  const label = (id: string) => labels.find((scope) => scope.id === id)?.label ?? id;

  return (
    // Two rows at every width: name and revoke, then scopes and last use.
    <div className="flex min-w-0 flex-col gap-1 pl-8 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 truncate font-medium">{token.name}</span>
        <span className="shrink-0 font-mono text-muted-foreground">{token.prefix}…</span>
        <Button
          variant="ghost"
          className={cn("ml-auto shrink-0", TAP_ON_PHONE)}
          aria-label={`Revoke ${token.name}`}
          onClick={() => setConfirming(true)}
        >
          Revoke
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {token.scopes.map((scope) => (
          <Badge key={scope} variant="secondary">
            {label(scope)}
          </Badge>
        ))}
        <span className="text-muted-foreground">
          {token.last_used_at ? `Last used ${formatDateTime(token.last_used_at)}` : "Never used"}
        </span>
      </div>
      {revoke.isError && (
        <p role="alert" className="text-xs text-destructive">
          {errorMessage(revoke.error)}
        </p>
      )}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent aria-describedby={undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke {token.name}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={TAP_ON_PHONE}>Cancel</AlertDialogCancel>
            <AlertDialogAction className={TAP_ON_PHONE} onClick={() => revoke.mutate(token.id)}>
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
